/**
 * Procedural placeholder samples: each registry recipe is rendered once per
 * variant into an AudioBuffer with an OfflineAudioContext, then cached and
 * played exactly like a decoded file.
 */
import type { SynthLayer } from './types';

const RATE = 22050;
/** Every placeholder is normalized to this peak (-3 dBFS); loudness comes from the registry volume, like sounds.json. */
export const PEAK = 0.708;

export function normalize(buf: AudioBuffer, peak: number): void {
  let m = 0;
  for (let c = 0; c < buf.numberOfChannels; c++) for (const v of buf.getChannelData(c)) m = Math.max(m, Math.abs(v));
  if (!(m > 1e-6) || !Number.isFinite(m)) return;
  const k = peak / m;
  for (let c = 0; c < buf.numberOfChannels; c++) { const d = buf.getChannelData(c); for (let i = 0; i < d.length; i++) d[i] *= k; }
}

function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

function noiseBuffer(ctx: BaseAudioContext, dur: number, color: 'white' | 'brown', rnd: () => number, clicks = 0): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.max(1, Math.ceil(dur * sr));
  const buf = ctx.createBuffer(1, n, sr);
  const d = buf.getChannelData(0);
  let last = 0;
  if (clicks > 0) {
    // granular crunch: short decaying grains at random times with random levels
    for (let c = 0; c < clicks; c++) {
      const at = Math.floor(Math.pow(rnd(), 1.3) * n * 0.85);
      const len = Math.floor(sr * (0.002 + rnd() * 0.006));
      const amp = 0.4 + rnd() * 0.6;
      for (let i = 0; i < len && at + i < n; i++) d[at + i] += (rnd() * 2 - 1) * amp * Math.exp(-5 * i / len);
    }
    return buf;
  }
  for (let i = 0; i < n; i++) {
    const w = rnd() * 2 - 1;
    if (color === 'brown') { last = (last + 0.04 * w) / 1.04; d[i] = last * 3.5; } else d[i] = w;
  }
  return buf;
}

/** Linear attack, optional hold, exponential decay to silence at t0 + dur. */
function envelope(g: GainNode, t0: number, dur: number, a: number, peak: number, hold = 0): void {
  const p = g.gain;
  p.setValueAtTime(0.0001, t0);
  p.linearRampToValueAtTime(peak, t0 + a);
  if (hold >= 1) { p.setValueAtTime(peak, t0); return; } // loop material: flat
  const h = t0 + a + Math.max(0, dur - a) * hold;
  p.setValueAtTime(peak, h);
  p.exponentialRampToValueAtTime(0.0001, t0 + dur);
}

function addAm(ctx: BaseAudioContext, target: GainNode, t0: number, dur: number, am?: [number, number]): AudioNode {
  if (!am) return target;
  const m = ctx.createGain();
  m.gain.value = 1 - am[1] / 2;
  const lfo = ctx.createOscillator();
  lfo.frequency.value = am[0];
  const depth = ctx.createGain();
  depth.gain.value = am[1] / 2;
  lfo.connect(depth).connect(m.gain);
  lfo.start(t0); lfo.stop(t0 + dur + 0.01);
  m.connect(target);
  return m;
}

function layer(ctx: OfflineAudioContext, L: SynthLayer, rnd: () => number, out: AudioNode): void {
  const j = (v: number, amt: number): number => v * (1 + (rnd() * 2 - 1) * amt);
  const flat = (L.hold ?? 0) >= 1; // loop material keeps exact timing
  const t0 = flat ? (L.t ?? 0) : (L.t ?? 0) * j(1, 0.15);
  const dur = flat ? L.dur : j(L.dur, 0.15);
  const env = ctx.createGain();
  envelope(env, t0, dur, Math.min(L.a ?? 0.003, dur * 0.9), L.g ?? 1, L.hold ?? 0);
  env.connect(out);
  const head = addAm(ctx, env, t0, dur, L.am);
  if (L.k === 'noise') {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, dur + 0.02, L.color ?? 'white', rnd, L.clicks ? Math.max(1, Math.round(j(L.clicks, 0.3))) : 0);
    let node: AudioNode = src;
    if (L.filt) {
      const f = ctx.createBiquadFilter();
      f.type = L.filt;
      const f0 = j(L.f ?? 1000, 0.12);
      f.frequency.setValueAtTime(f0, t0);
      if (L.f1) f.frequency.exponentialRampToValueAtTime(j(L.f1, 0.08), t0 + dur);
      f.Q.value = L.q ?? 1;
      node.connect(f);
      node = f;
    }
    node.connect(head);
    src.start(t0); src.stop(t0 + dur + 0.01);
  } else {
    const o = ctx.createOscillator();
    o.type = L.wave;
    const f0 = j(L.f, 0.05);
    o.frequency.setValueAtTime(f0, t0);
    if (L.f1) o.frequency.exponentialRampToValueAtTime(Math.max(1, j(L.f1, 0.05)), t0 + dur);
    if (L.vib) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = L.vib[0];
      const d = ctx.createGain();
      d.gain.value = L.vib[1];
      lfo.connect(d).connect(o.frequency);
      lfo.start(t0); lfo.stop(t0 + dur + 0.01);
    }
    let node: AudioNode = o;
    if (L.formants) {
      const sum = ctx.createGain();
      for (const [ff, q] of L.formants) {
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass'; bp.frequency.value = j(ff, 0.06); bp.Q.value = q;
        o.connect(bp).connect(sum);
      }
      sum.gain.value = 2.5;
      node = sum;
    }
    if (L.filt) {
      const f = ctx.createBiquadFilter();
      f.type = L.filt;
      f.frequency.value = L.ff ?? 1000;
      f.Q.value = L.q ?? 1;
      node.connect(f);
      node = f;
    }
    node.connect(head);
    o.start(t0); o.stop(t0 + dur + 0.01);
  }
}

/** Render one variant of a recipe; deterministic per (id, variant). */
export async function renderSynth(id: string, variant: number, layers: SynthLayer[]): Promise<AudioBuffer> {
  const rnd = mulberry(hash(id) + variant * 7919);
  let end = 0;
  for (const L of layers) end = Math.max(end, (L.t ?? 0) * 1.16 + L.dur * 1.16);
  const ctx = new OfflineAudioContext(1, Math.ceil((end + 0.05) * RATE), RATE);
  const out = ctx.createGain();
  out.gain.value = 0.8;
  // soft clip so stacked layers never wrap
  const shaper = ctx.createWaveShaper();
  const curve = new Float32Array(1024);
  for (let i = 0; i < curve.length; i++) { const x = (i / 511.5) - 1; curve[i] = Math.tanh(x * 1.5) / Math.tanh(1.5); }
  shaper.curve = curve;
  out.connect(shaper).connect(ctx.destination);
  // lo-fi: vanilla's samples read like dulled, downsampled recordings
  const lofi = ctx.createBiquadFilter();
  lofi.type = 'lowpass'; lofi.frequency.value = 8500; lofi.Q.value = 0.5;
  for (const L of layers) layer(ctx, L, rnd, lofi);
  lofi.connect(out);
  const buf = await ctx.startRendering();
  normalize(buf, PEAK);
  return buf;
}

/** Stereo reverb impulse: decaying noise with a darkening tail (procedural, no files). */
export function makeImpulse(ctx: BaseAudioContext, seconds: number, decay: number): AudioBuffer {
  const n = Math.ceil(seconds * ctx.sampleRate);
  const buf = ctx.createBuffer(2, n, ctx.sampleRate);
  const rnd = mulberry(1234);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const t = i / n;
      const k = 0.15 + 0.8 * (1 - t); // low-pass gets stronger as the tail ages
      lp += (rnd() * 2 - 1 - lp) * k;
      d[i] = lp * Math.pow(1 - t, decay);
    }
  }
  return buf;
}

/** Crossfade the tail into the head so a buffer loops without a seam. */
export function makeLoopable(ctx: BaseAudioContext, buf: AudioBuffer, fadeSec = 0.4): AudioBuffer {
  const f = Math.min(Math.floor(fadeSec * buf.sampleRate), Math.floor(buf.length / 3));
  const n = buf.length - f;
  const out = ctx.createBuffer(buf.numberOfChannels, n, buf.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const a = buf.getChannelData(c), o = out.getChannelData(c);
    for (let i = 0; i < n; i++) o[i] = a[i];
    for (let i = 0; i < f; i++) { const k = i / f; o[i] = a[i] * Math.sqrt(k) + a[n + i] * Math.sqrt(1 - k); }
  }
  return out;
}
